// Centralized API + WS URL helpers
const RAW = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${RAW}/api`;
// Build a ws:// or wss:// URL aligned with the backend protocol
export const WS_BASE = (() => {
  if (!RAW) return "";
  const u = new URL(RAW);
  const proto = u.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${u.host}/api/ws`;
})();

async function request(path, { method = "GET", body, token, signal } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });
  // Read body once, but tolerate the case where the platform layer (some
  // Kubernetes/Cloudflare paths) hands us a Response whose body has already
  // been consumed for non-2xx responses. In that case we fall back to a
  // status-code-derived message so callers still see something meaningful.
  let raw = "";
  try {
    raw = await res.text();
  } catch {
    raw = "";
  }
  let data = null;
  if (raw) {
    try { data = JSON.parse(raw); } catch { /* leave as null */ }
  }
  if (!res.ok) {
    const detail =
      (data && (data.detail || data.message)) ||
      raw ||
      genericMessageForStatus(res.status);
    throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
  }
  return data;
}

function genericMessageForStatus(status) {
  if (status === 400) return "That request looks invalid.";
  if (status === 401) return "Please sign in again.";
  if (status === 403) return "You don't have access to this.";
  if (status === 404) return "Not found.";
  if (status === 409) return "That conflicts with something — please refresh and try again.";
  if (status === 410) return "This is no longer available.";
  if (status >= 500) return "Server error — please try again in a moment.";
  return `HTTP ${status}`;
}

export const api = {
  // Admin
  adminLogin: (passcode) => request("/admin/login", { method: "POST", body: { passcode } }),
  adminCreateLesson: (token, body) => request("/admin/lessons", { method: "POST", body, token }),
  adminListLessons: (token) => request("/admin/lessons", { token }),
  adminGetLesson: (token, id) => request(`/admin/lessons/${id}`, { token }),
  adminDeleteLesson: (token, id) => request(`/admin/lessons/${id}`, { method: "DELETE", token }),
  adminGetRecording: (token, id) => request(`/admin/lessons/${id}/recording`, { token }),
  adminResendInvites: (token, id) => request(`/admin/lessons/${id}/resend-invites`, { method: "POST", token }),
  // Admin availability
  adminListTemplates: (token) => request("/admin/availability/templates", { token }),
  adminCreateTemplate: (token, body) => request("/admin/availability/templates", { method: "POST", body, token }),
  adminUpdateTemplate: (token, id, body) => request(`/admin/availability/templates/${id}`, { method: "PATCH", body, token }),
  adminDeleteTemplate: (token, id) => request(`/admin/availability/templates/${id}`, { method: "DELETE", token }),
  // Public availability
  listSlots: (start, end) => request(`/availability/slots?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`),
  nextAvailableSlot: () => request("/availability/next-slot"),

  // Contact form
  submitContact: (body) => request("/contact", { method: "POST", body }),

  // Partnerships
  listPartnerships: (kind) => request(`/partnerships${kind ? `?kind=${kind}` : ""}`),
  adminListPartnerships: (token, kind) =>
    request(`/admin/partnerships${kind ? `?kind=${kind}` : ""}`, { token }),
  adminCreatePartnership: (token, body) =>
    request("/admin/partnerships", { method: "POST", body, token }),
  adminUpdatePartnership: (token, id, body) =>
    request(`/admin/partnerships/${id}`, { method: "PATCH", body, token }),
  adminDeletePartnership: (token, id) =>
    request(`/admin/partnerships/${id}`, { method: "DELETE", token }),
  // Students
  studentRegister: (email, password, name) => request("/students/register", { method: "POST", body: { email, password, name } }),
  studentLogin: (email, password) => request("/students/login", { method: "POST", body: { email, password } }),
  studentForgotPassword: (email) => request("/students/forgot-password", { method: "POST", body: { email } }),
  studentResetPassword: (token, new_password) => request("/students/reset-password", { method: "POST", body: { token, new_password } }),
  studentMe: (token) => request("/students/me", { token }),
  studentUpdateMe: (token, body) => request("/students/me", { method: "PATCH", body, token }),
  studentChangePassword: (token, current_password, new_password) =>
    request("/students/me/password", { method: "POST", body: { current_password, new_password }, token }),
  studentMyLessons: (token) => request("/students/me/lessons", { token }),
  // Cart
  cartGet: (token) => request("/cart", { token }),
  cartAdd: (token, start, end) => request("/cart/items", { method: "POST", body: { start, end }, token }),
  cartRemove: (token, holdId) => request(`/cart/items/${holdId}`, { method: "DELETE", token }),
  cartClear: (token) => request("/cart", { method: "DELETE", token }),
  // Checkout
  checkoutSession: (token, originUrl) => request("/checkout/session", { method: "POST", body: { origin_url: originUrl }, token }),
  checkoutStatus: (token, sessionId) => request(`/checkout/status/${sessionId}`, { token }),
  // Teacher
  teacherLogin: (email, password) => request("/teachers/login", { method: "POST", body: { email, password } }),
  teacherForgotPassword: (email) => request("/teachers/forgot-password", { method: "POST", body: { email } }),
  teacherResetPassword: (token, new_password) => request("/teachers/reset-password", { method: "POST", body: { token, new_password } }),
  teacherMe: (token) => request("/teachers/me", { token }),
  teacherUpdateMe: (token, body) => request("/teachers/me", { method: "PATCH", body, token }),
  teacherChangePassword: (token, current_password, new_password) =>
    request("/teachers/me/password", { method: "POST", body: { current_password, new_password }, token }),
  teacherMyLessons: (token) => request("/teachers/me/lessons", { token }),
  teacherJoinLesson: (token, lessonId) =>
    request(`/teachers/me/lessons/${lessonId}/join`, { method: "POST", token }),
  // Admin teachers
  adminListTeachers: (token) => request("/admin/teachers", { token }),
  adminCreateTeacher: (token, body) => request("/admin/teachers", { method: "POST", body, token }),
  adminUpdateTeacher: (token, id, body) => request(`/admin/teachers/${id}`, { method: "PATCH", body, token }),
  adminDeleteTeacher: (token, id) => request(`/admin/teachers/${id}`, { method: "DELETE", token }),
  adminResetTeacherPassword: (token, id) =>
    request(`/admin/teachers/${id}/reset-password`, { method: "POST", token }),
  adminAssignTeacher: (token, lessonId, teacherId) =>
    request(`/admin/lessons/${lessonId}/assign-teacher`, { method: "POST", body: { teacher_id: teacherId }, token }),
  // Classroom
  join: (passcode) => request("/classroom/join", { method: "POST", body: { passcode } }),
  feedback: (passcode, rating, comments) =>
    request("/classroom/feedback", { method: "POST", body: { passcode, rating, comments } }),
  studentRecording: (passcode) =>
    request("/classroom/recording", { method: "POST", body: { passcode } }),

  // ---------- Lesson materials ----------
  // Student
  studentListMaterials: (token) => request("/students/me/materials", { token }),
  studentUploadMaterial: (token, lessonId, file) =>
    uploadFile(`/students/me/lessons/${lessonId}/materials`, token, file),
  studentDeleteMaterial: (token, materialId) =>
    request(`/students/me/materials/${materialId}`, { method: "DELETE", token }),
  studentMaterialFileUrl: (token, materialId) =>
    `${API_BASE}/students/me/materials/${materialId}/file?token=${encodeURIComponent(token)}`,
  // Teacher
  teacherListLessonMaterials: (token, lessonId) =>
    request(`/teachers/me/lessons/${lessonId}/materials`, { token }),
  teacherUploadMaterial: (token, lessonId, file) =>
    uploadFile(`/teachers/me/lessons/${lessonId}/materials`, token, file),
  teacherDeleteMaterial: (token, materialId) =>
    request(`/teachers/me/materials/${materialId}`, { method: "DELETE", token }),
  teacherMaterialFileUrl: (token, materialId) =>
    `${API_BASE}/teachers/me/materials/${materialId}/file?token=${encodeURIComponent(token)}`,
  // Recordings (student)
  studentRecordingStreamUrl: (token, lessonId) =>
    `${API_BASE}/students/me/recordings/${lessonId}/stream?token=${encodeURIComponent(token)}`,
  studentDeleteRecording: (token, lessonId) =>
    request(`/students/me/recordings/${lessonId}`, { method: "DELETE", token }),
  // Recordings (admin)
  adminDeleteRecording: (token, lessonId) =>
    request(`/admin/lessons/${lessonId}/recording`, { method: "DELETE", token }),
};

// Multipart upload helper. Keeps `request()` clean of FormData branching.
async function uploadFile(path, token, file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  let raw = "";
  try { raw = await res.text(); } catch { raw = ""; }
  let data = null;
  if (raw) { try { data = JSON.parse(raw); } catch { /* leave null */ } }
  if (!res.ok) {
    const detail =
      (data && (data.detail || data.message)) ||
      raw ||
      genericMessageForStatus(res.status);
    throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
  }
  return data;
}
